import React, { useCallback, useEffect, useRef } from 'react';
import {
  AlertActionType,
  AlertPosition,
  AlertType,
  AlertUrgency,
  closeAlert,
  Icon,
  FdsIcon,
  showAlert,
  ShowAlertBannerDisplay,
} from '@tcc/fds';
import { getMessage } from '@tcc/i18n';
import { UrlPlaceholders } from './useNavigationLinks/types';
import { User } from '../types/user';
import { useExpertoBannerPreference } from './useExpertoBannerPreference';

const EXPERTO_IMPORT_FIELDS_URL = `${UrlPlaceholders.EXPERTO_URL}/import-fields`;

interface UseExpertoBannerParams {
  user?: User;
  featureFlags: Record<string, boolean | undefined>;
  containerElement?: HTMLDivElement | null;
  onOpenDialog?: () => void;
  onCloseDialog?: () => void;
}

export const useExpertoBanner = ({
  user,
  featureFlags,
  containerElement,
  onOpenDialog,
  onCloseDialog,
}: UseExpertoBannerParams) => {
  const hasTriggeredRef = useRef(false);
  const hasFetchedPreferenceRef = useRef(false);
  const bannerToastIdRef = useRef<string | number | null>(null);
  const isArgentinaUser = user && user.details && user.details.countryCode === 'AR';
  const isExpertoBannerEnabledForUser =
    !!user?.uuid
    && featureFlags?.['web.fieldview.experto-banner'] === true
    && isArgentinaUser;

  const {
    fetchPreference,
    showBanner,
    preferenceId,
    updatePreference,
  } = useExpertoBannerPreference();

  useEffect(() => {
    if (!isExpertoBannerEnabledForUser || !user?.uuid) {
      hasFetchedPreferenceRef.current = false;
      return;
    }

    if (hasFetchedPreferenceRef.current) {
      return;
    }

    hasFetchedPreferenceRef.current = true;
    fetchPreference({ variables: { userId: user.uuid.toString() } }).catch(() => {
      hasFetchedPreferenceRef.current = false;
    });
  }, [fetchPreference, isExpertoBannerEnabledForUser, user]);

  const handleConfirmHideExpertoBanner = useCallback(() => {
    onCloseDialog?.();

    if (bannerToastIdRef.current !== null) {
      closeAlert(bannerToastIdRef.current);
      bannerToastIdRef.current = null;
    }

    if (!user?.uuid || !preferenceId) {
      return;
    }

    updatePreference({
      variables: {
        userId: user.uuid.toString(),
        preferenceId,
        showBanner: false,
      },
    }).catch(() => {
      hasTriggeredRef.current = false;
    });
  }, [onCloseDialog, preferenceId, updatePreference, user]);

  useEffect(() => {
    if (hasTriggeredRef.current) {
      return;
    }

    if (
      containerElement
      && isExpertoBannerEnabledForUser
      && showBanner
    ) {
      hasTriggeredRef.current = true;

      const alertContainerRef = { current: containerElement } as React.RefObject<HTMLDivElement>;

      const toastId = showAlert({
        position: AlertPosition.Banner,
        urgency: AlertUrgency.Urgent,
        type: AlertType.Warning,
        dismissible: false,
        bannerDisplay: ShowAlertBannerDisplay.PushContent,
        onDismiss: () => {
          bannerToastIdRef.current = null;
        },
        children: getMessage('experto.alert.label'),
        containerRef: alertContainerRef,
        actions: [
          {
            type: AlertActionType.Secondary,
            label: getMessage('experto.alert.dismiss'),
            testId: 'experto-alert-banner-secondary',
            buttonProps: {
              onClick: onOpenDialog,
            },
          },
          {
            type: AlertActionType.Primary,
            label: <>
              <Icon icon={FdsIcon.JumpToElement} />
              {getMessage('experto.alert.importFields')}
            </>,
            testId: 'experto-alert-banner-primary',
            buttonProps: {
              onClick: () => window.location.assign(EXPERTO_IMPORT_FIELDS_URL),
            },
          },
        ],
        persistent: true,
      });

      bannerToastIdRef.current = toastId;
    }
  }, [
    containerElement,
    isExpertoBannerEnabledForUser,
    onOpenDialog,
    showBanner,
  ]);

  return { handleConfirmHideExpertoBanner };
};
